import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CurrentDelayData } from "../models/CurrentDelayData";
import { getCurrentDelay } from "../repositories/StatusBarRepository";

interface UseCurrentDelayOptions {
	refreshInterval?: number;
	enabled?: boolean;
}

interface UseCurrentDelayResult extends Partial<CurrentDelayData> {
	isLoading: boolean;
	error: Error | null;
	currentDelayTimeString: string;
	shiftDelayTimeString: string;
	barsInChainsLabel: string;
}

const secondsToTimeString = (seconds: number): string => {
	if (seconds <= 0) return "00:00:00";
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = Math.floor(seconds % 60);
	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const useCurrentDelay = ({
	refreshInterval = 5000,
	enabled = true,
}: UseCurrentDelayOptions = {}): UseCurrentDelayResult => {
	const [localCurrentSeconds, setLocalCurrentSeconds] = useState<number>(0);
	const [localShiftSeconds, setLocalShiftSeconds] = useState<number>(0);

	const {
		data,
		isLoading,
		error: queryError,
	} = useQuery({
		queryKey: ["currentDelay"],
		queryFn: getCurrentDelay,
		refetchInterval: refreshInterval,
		enabled,
		retry: 1,
	});

	// Sync local counters when server data arrives
	useEffect(() => {
		if (data) {
			setLocalCurrentSeconds(data.currentDelaySeconds ?? 0);
			setLocalShiftSeconds(data.shiftDelaySeconds ?? 0);
		}
	}, [data]);

	// Local 1-second counter for smooth timer updates
	useEffect(() => {
		if (!data?.delayActive) return;

		const interval = setInterval(() => {
			setLocalCurrentSeconds((prev) => prev + 1);
			setLocalShiftSeconds((prev) => prev + 1);
		}, 1000);

		return () => clearInterval(interval);
	}, [data?.delayActive]);

	return {
		...data,
		currentDelaySeconds: localCurrentSeconds,
		shiftDelaySeconds: localShiftSeconds,
		currentDelayTimeString: secondsToTimeString(localCurrentSeconds),
		shiftDelayTimeString: secondsToTimeString(localShiftSeconds),
		barsInChainsLabel: data
			? `${data.barsInChains}/${data.barsInChainsSP}`
			: "-",
		isLoading,
		error: queryError as Error | null,
	};
};
